import { useEffect, useState } from "react";
import { Wallet } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtMYT } from "@/lib/myt";

const W_STATUS = {
  PENDING: "text-[#D4AF37] border-[#D4AF37]/30 bg-[#D4AF37]/10",
  COMPLETED: "text-emerald-400 border-emerald-400/30 bg-emerald-400/10",
  PAID: "text-emerald-400 border-emerald-400/30 bg-emerald-400/10",
  REJECTED: "text-red-400 border-red-400/30 bg-red-400/10",
  CANCELLED: "text-[#94A3B8] border-[#222530] bg-[#222530]/40",
};

export default function IBWallet() {
  const [wallet, setWallet] = useState(null);
  const [form, setForm] = useState({ amount: "", full_name: "", bank_account_number: "", bank_name: "" });
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/ib/wallet").then((res) => setWallet(res.data)).catch(() => setWallet(null));
  };

  useEffect(() => {
    load();
  }, []);

  const canWithdraw = wallet && wallet.available_balance >= wallet.min_withdrawal;

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (!form.amount || Number(form.amount) <= 0) return "Enter a withdrawal amount.";
    if (wallet && Number(form.amount) < wallet.min_withdrawal) return `Minimum withdrawal is $${wallet.min_withdrawal} USD.`;
    if (wallet && Number(form.amount) > wallet.available_balance) return "Amount exceeds your available balance.";
    if (form.full_name.trim().length < 3) return "Enter your full name as per your bank account.";
    if (!/^\d{5,20}$/.test(form.bank_account_number.replace(/\s+/g, ""))) return "Bank account number must be 5–20 digits.";
    if (form.bank_name.trim().length < 2) return "Enter your bank name.";
    return null;
  };

  const submitWithdraw = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/ib/wallet/withdraw", {
        amount: Number(form.amount),
        full_name: form.full_name.trim(),
        bank_account_number: form.bank_account_number.replace(/\s+/g, ""),
        bank_name: form.bank_name.trim(),
      });
      toast.success(res.data.message);
      setShowWithdraw(false);
      setForm({ amount: "", full_name: "", bank_account_number: "", bank_name: "" });
      load();
    } catch (err2) {
      toast.error(formatApiError(err2));
    }
    setBusy(false);
  };

  const cancelWithdrawal = async (w) => {
    try {
      await api.post(`/ib/withdrawals/${w.id}/cancel`);
      toast.success("Withdrawal request cancelled");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="ib-wallet-page">
      <div>
        <p className="eyebrow-label">Earnings</p>
        <h1 className="page-title mt-2">IB Wallet</h1>
        <p className="page-sub mt-1">Your IB earnings come from two sources: 20% commission on PLUSMAKER product purchases made by your clients, and 80% earnings from your own Live & Learn products purchased by your clients. All earnings are credited directly to your IB Wallet. Withdrawals are processed manually by administration.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="ib-wallet-stats">
        {[
          ["Available Balance", wallet?.available_balance, "ib-wallet-balance"],
          ["Total Earnings", wallet?.total_earnings, "ib-wallet-earnings"],
          ["Pending Withdrawal", wallet?.pending_withdrawal, "ib-wallet-pending"],
        ].map(([label, val, testid]) => (
          <div key={label} className="card-lux p-5">
            <p className="text-[10px] font-mono tracking-widest uppercase text-[#64748B]">{label}</p>
            <p className="text-2xl font-bold text-[#D4AF37] mt-1.5 font-mono" data-testid={testid}>
              {val === undefined || val === null ? "—" : `$${val.toFixed(2)}`}
            </p>
          </div>
        ))}
      </div>

      <div className="card-lux p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="ib-withdraw-section">
        <div>
          <p className="text-sm text-white font-medium">Withdraw Funds</p>
          <p className="text-[11px] text-[#64748B] mt-1">
            Minimum withdrawal: ${wallet?.min_withdrawal ?? 50} USD.
            {!canWithdraw && wallet && ` You need $${(wallet.min_withdrawal - wallet.available_balance).toFixed(2)} more to unlock withdrawals.`}
          </p>
        </div>
        <button
          onClick={() => setShowWithdraw(true)}
          disabled={!canWithdraw}
          className="btn-gold px-6 py-2.5 text-xs disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          data-testid="ib-withdraw-button"
        >
          <Wallet className="h-3.5 w-3.5" /> Request Withdrawal
        </button>
      </div>

      <section className="space-y-3" data-testid="ib-commissions-section">
        <p className="eyebrow-label">Commission History</p>
        <div className="card-lux overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ib-commissions-table">
              <thead>
                <tr className="border-b border-[#222530] text-left">
                  <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide">Date</th>
                  <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide">Client</th>
                  <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide">Plan</th>
                  <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide">Payment</th>
                  <th className="px-5 py-4 text-xs font-medium text-[#64748B] tracking-wide text-right">Commission (20%)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#222530]">
                {!wallet || wallet.commissions.length === 0 ? (
                  <tr><td colSpan={5} className="px-5 py-10 text-center text-[#64748B]" data-testid="ib-commissions-empty">No commissions yet. You earn 20% when a client under you purchases a paid product with COINS.</td></tr>
                ) : (
                  wallet.commissions.map((tx) => (
                    <tr key={tx.id} data-testid={`ib-commission-row-${tx.id.slice(-6)}`}>
                      <td className="px-5 py-4 text-[#94A3B8] text-xs font-mono">{fmtMYT(tx.created_at)}</td>
                      <td className="px-5 py-4">
                        <p className="text-white text-xs">{tx.client_name}</p>
                        <p className="text-[10px] text-[#64748B] font-mono">{tx.client_uid}</p>
                      </td>
                      <td className="px-5 py-4 text-[#D4AF37] text-xs font-mono">{tx.plan_name}</td>
                      <td className="px-5 py-4 text-[#94A3B8] text-xs font-mono">${tx.gross_amount?.toFixed(2)}</td>
                      <td className="px-5 py-4 text-right text-emerald-400 text-xs font-mono">+${tx.amount?.toFixed(2)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="space-y-3" data-testid="ib-withdrawals-section">
        <p className="eyebrow-label">Withdrawal History</p>
        <div className="card-lux divide-y divide-[#222530]">
          {!wallet || wallet.withdrawals.length === 0 ? (
            <p className="px-5 py-8 text-center text-[#64748B] text-sm" data-testid="ib-withdrawals-empty">No withdrawals yet.</p>
          ) : (
            wallet.withdrawals.map((w) => (
              <div key={w.id} className="flex items-center gap-4 px-5 py-4 flex-wrap" data-testid={`ib-withdrawal-row-${w.id.slice(-6)}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-mono">${w.amount?.toFixed(2)} {w.currency}</p>
                  {w.bank_name && (
                    <p className="text-[10px] text-[#64748B] font-mono mt-0.5">{w.full_name} · {w.bank_name} · {w.bank_account_number}</p>
                  )}
                  <p className="text-[10px] text-[#64748B] font-mono mt-0.5">
                    Requested {fmtMYT(w.created_at)}{w.decided_at ? ` · ${w.status === "COMPLETED" || w.status === "PAID" ? "Completed" : "Decided"} ${fmtMYT(w.decided_at)}` : ""}
                  </p>
                  {w.reject_reason && <p className="text-[10px] text-red-400/90 mt-0.5">Reason: {w.reject_reason}</p>}
                </div>
                <span className={`text-[10px] font-mono tracking-widest uppercase rounded-full px-2.5 py-1 border ${W_STATUS[w.status] || "text-[#94A3B8] border-[#222530]"}`} data-testid={`ib-withdrawal-status-${w.id.slice(-6)}`}>{w.status}</span>
                {w.status === "PENDING" && (
                  <button onClick={() => cancelWithdrawal(w)} className="text-[11px] text-red-400 border border-red-400/30 hover:bg-red-400/10 rounded-lg px-2.5 py-1.5 transition-colors" data-testid={`ib-withdrawal-cancel-${w.id.slice(-6)}`}>
                    Cancel
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      </section>

      {showWithdraw && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowWithdraw(false)} />
          <div className="relative card-lux w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="ib-withdraw-modal">
            <p className="eyebrow-label">Request Withdrawal</p>
            <p className="text-xs text-[#64748B]">Available: <span className="text-[#D4AF37] font-mono">${wallet?.available_balance?.toFixed(2)}</span> · Minimum ${wallet?.min_withdrawal} · processed manually by administration.</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-1.5 tracking-wide">Withdrawal Amount (USD) <span className="text-red-400">*</span></label>
                <input type="number" min={wallet?.min_withdrawal} step="0.01" className="input-lux" value={form.amount} onChange={setField("amount")} placeholder="100.00" data-testid="ib-withdraw-amount-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-1.5 tracking-wide">Full Name <span className="text-red-400">*</span></label>
                <input className="input-lux" value={form.full_name} onChange={setField("full_name")} placeholder="Ahmad bin Ali" data-testid="ib-withdraw-name-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-1.5 tracking-wide">Bank Account Number <span className="text-red-400">*</span></label>
                <input className="input-lux" value={form.bank_account_number} onChange={setField("bank_account_number")} placeholder="1234567890" data-testid="ib-withdraw-account-input" />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#94A3B8] mb-1.5 tracking-wide">Bank Name <span className="text-red-400">*</span></label>
                <input className="input-lux" value={form.bank_name} onChange={setField("bank_name")} placeholder="Maybank" data-testid="ib-withdraw-bank-input" />
              </div>
            </div>
            <p className="text-[11px] text-[#64748B] italic leading-relaxed border-t border-[#222530] pt-3" data-testid="ib-withdraw-remark">
              Withdrawal conversion rates may vary based on the current USD market rate. A processing adjustment of approximately 10%–20% may apply.
            </p>
            <div className="flex gap-3">
              <button onClick={submitWithdraw} disabled={busy} className="btn-gold flex-1 py-2.5 text-xs disabled:opacity-50" data-testid="ib-withdraw-confirm-button">
                {busy ? "Submitting…" : "Submit Request"}
              </button>
              <button onClick={() => setShowWithdraw(false)} className="btn-gold-outline px-5 py-2.5 text-xs" data-testid="ib-withdraw-cancel-button">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
